import {
    autoinject,
    bindable,
    bindingMode,
    computedFrom,
    containerless,
    customElement,
    observable
} from "aurelia-framework";

import nameof from "../../../../common/nameof";
import { FormValidationEnum } from "../../../../enums/form-validation-enum";
import { IOption } from "../../../../interfaces/form-builder/i-element";
import { IAncestorSettings, IInputOptions } from "../../../../interfaces/form-builder/i-input-options";
import { IConditionalValidation, IValidation } from "../../../../interfaces/form-builder/i-validation";
import { ITypeaheadOptions } from "../../../../interfaces/i-typeahead";
import { IValidateCustomElement } from "../../../../interfaces/i-validate-custom-element";
import { FormBuilderEvent, NoteDataManager } from "../../note-data-manager";
import { EventAggregator } from "aurelia-event-aggregator";
import { IAutofillData } from "../../../../interfaces/form-builder/i-autofill-data";
import { FormConditionValidationEnum } from "../../../../enums/form-condition-validation-enum";

@customElement("single-select-dropdown")
@containerless
@autoinject
export class SingleSelectDropDown {
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public result: any;
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public viewOptions: IInputOptions;
    @bindable({ defaultBindingMode: bindingMode.toView })
    public ancestorSettings: IAncestorSettings;
    private _ea: EventAggregator;
    @observable({
        changeHandler: nameof<SingleSelectDropDown>("typeaheadResultChanged")
    })
    public typeaheadResult: ITypeaheadOptions;
    public noteDataManager: NoteDataManager;
    public isValidationLoading: boolean = true;
    public validation: IValidateCustomElement;

    @computedFrom(`${nameof<SingleSelectDropDown>("viewOptions")}.${nameof<IInputOptions>("options")}`)
    public get typeaheadOptions(): ITypeaheadOptions[] {
        let result: ITypeaheadOptions[] = [];
        if (this.viewOptions) {
            let options = this.viewOptions.options;
            if (options && options.length > 0) {
                result = options.map((option: IOption) => ({
                    name: option.label,
                    value: option.value
                }));
            }
        }
        return result;
    }
    public set typeaheadOptions(data: ITypeaheadOptions[]) {
        // Dummy setter for 2 way binding property
    }

    public constructor(noteDataManager: NoteDataManager, eventAggregator: EventAggregator) {
        this.noteDataManager = noteDataManager;
        this._ea = eventAggregator;
    }

    public attached() {
        this.initValidation();
        this._ea.subscribe(FormBuilderEvent.AutofillData, (event: IAutofillData) => {
            if (
                event?.component &&
                this.viewOptions.name &&
                event?.component.toLowerCase() === this.viewOptions?.name.toLowerCase() &&
                !event.data
            ) {
                this.result = "";
                this.typeaheadResult = null;
            }
        });
    }

    public initValidation() {
        let validation = this.noteDataManager.getValidationFromProp(this.viewOptions.name);
        validation?.forEach((rule: IValidation | IConditionalValidation) => {
            if (rule.validationType === FormValidationEnum.Required) {
                this.validation = {
                    required: true,
                    displayName: this.viewOptions.name,
                    message: rule.message
                };
            } else if (rule.validationType === FormValidationEnum.Conditional) {
                let conditionalRule = rule as IConditionalValidation;
                this.validation = {
                    required: false,
                    displayName: this.viewOptions.name,
                    message: conditionalRule.conditionValidation.message
                };

                const updateConditionalValidation = () => {
                    let targetVal = this.noteDataManager.getValue(conditionalRule.targetName);
                    if (conditionalRule.conditionType === FormConditionValidationEnum.IsNotEmpty) {
                        this.validation.required = !!targetVal;
                    } else if (conditionalRule.conditionType === FormConditionValidationEnum.IsEmpty) {
                        this.validation.required = !targetVal;
                    }
                };

                updateConditionalValidation();

                this._ea.subscribe("resultChanged", (targetName: string) => {
                    if (targetName === conditionalRule.targetName) {
                        updateConditionalValidation();
                    }
                });
            }
        });
        this.isValidationLoading = false;
    }

    public typeaheadResultChanged(newValue: ITypeaheadOptions, oldValue: ITypeaheadOptions) {
        if (newValue || oldValue) {
            if (newValue) {
                this.result = newValue.value;
            } else {
                this.result = "";
            }
        }
    }

    public resultChanged(newValue: any) {
        if (!newValue) {
            return;
        }
        let resultOption = this.typeaheadOptions?.find((opt) => opt.value == this.result);
        if (resultOption) {
            this.typeaheadResult = resultOption;
        }
    }
}
